'use client'

import { useContext, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MdOutlineMenu, MdOutlineClose, MdOutlineArrowForward } from 'react-icons/md'
import { ThemeContext } from './ThemeContext';
import { AnimatedThemeToggler } from './ui/themeToggler';

function HeaderSmall({ onLetsTalk, isMenuOpen, setIsMenuOpen, isAISidebarOpen }) {

  const menuItems = [
    { title: "Home", number: "01", href: "#home" },
    { title: "About Me", number: "02", href: "#about" },
    { title: "Work", number: "03", href: "#works" },
    { title: "Tools", number: "04", href: "#insights" },
  ];

  const { isLight, handleMode } = useContext(ThemeContext);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleScroll = (id) => {
    const element = document.querySelector(id);
    toggleMenu()
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementTop - 120, // Adjust the top by your desired offset
        behavior: "smooth", // Smooth scroll effect
      });

    };
  }

  return (
    <header className="lg:hidden fixed block top-0 left-0 right-0 z-50 py-4 bg-zinc-50 dark:bg-black dark:bg-opacity-95 ">
      <div className="max-w-8xl mx-auto pl-6 pr-4 lg:px-20">
        <div className="flex items-center justify-between  lg:h-20">
          {/* Logo */}
          <motion.a
            href="/"
            className="text-lg  relative dark:text-zinc-400 text-zinc-900 font-normal"
          >
            vivek
            <span className="absolute -left-3 top-0 w-8 h-8 rounded-full dark:bg-zinc-800 bg-zinc-300 -z-10" />

            <span className="dark:text-zinc-100 text-zinc-400 tracking-wide">Khule</span>.dev
          </motion.a>

          {/* Navigation */}
          <motion.div className="flex items-center lg:gap-4 gap-1">
            <motion.button
              onClick={onLetsTalk}
              whileTap={{ scale: 0.95 }}
              animate={{
                boxShadow: [
                  "0 0 0px rgba(59, 130, 246, 0)",
                  "0 0 15px rgba(59, 130, 246, 0.3)",
                  "0 0 0px rgba(59, 130, 246, 0)"
                ]
              }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="group relative text-sm items-center lg:px-4 px-1 lg:py-2 py-1 rounded-full border dark:border-zinc-800 border-zinc-200 overflow-hidden bg-white dark:bg-zinc-900 dark:text-zinc-100 text-zinc-900 transition-all hover:border-zinc-400 dark:hover:border-zinc-500 shadow-sm"
            >
              {/* Continuous Shimmer Animation */}
              <div className="animate-shimmer z-0 pointer-events-none" />

              <div className="relative z-10 flex items-center gap-2">
                <div className="w-7 h-7 rounded-full overflow-hidden border border-zinc-200 dark:border-zinc-700 bg-zinc-100 shrink-0">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                    className="w-full h-full"
                  >
                    <img src={require('../assets/ailogo.png')} alt="AI" className="w-full h-full object-cover" />
                  </motion.div>
                </div>
                <span className="hidden sm:inline-block font-bold tracking-tight bg-gradient-to-r from-zinc-900 to-zinc-600 dark:from-white dark:to-zinc-400 bg-clip-text text-transparent">
                  My AI
                </span>
              </div>
            </motion.button>

            {/* Theme Toggle */}
            <AnimatedThemeToggler />

            {/* Menu Button */}
            <motion.button
              className="p-2 rounded-full dark:hover:bg-zinc-600 hover:bg-zinc-300 transition-colors"
              aria-label="Menu"
              onClick={toggleMenu}
            >
              {isMenuOpen ? (
                <MdOutlineClose className="w-6 h-6 dark:text-zinc-100 text-zinc-900" />
              ) : (
                <MdOutlineMenu className="w-6 h-6 dark:text-zinc-100 text-zinc-900" />
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Fullscreen Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="fixed inset-0 top-16 lg:top-28 dark:bg-black bg-zinc-50 z-40 overflow-y-auto scrollbar-hide"
          >
            <div className="max-w-8xl mx-auto px-4 lg:px-20 min-h-[calc(100vh-6rem)] flex flex-col gap-14 justify-between">
              <nav className="space-y-8 sm:space-y-10 lg:space-y-12 border-y-[1.5px] py-8 sm:py-10 dark:border-zinc-500 border-zinc-900">
                {menuItems.map((item, index) => (
                  <motion.div
                    key={item.number}
                    className="group"
                  >
                    <a
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault(); // Prevent the default link behavior
                        handleScroll(item.href); // Trigger scroll behavior
                      }}
                      className="flex items-center justify-between group relative"
                    >
                      <div className="flex items-baseline gap-4">
                        <span className="text-3xl sm:text-4xl lg:text-7xl font-bold dark:text-white text-zinc-900 dark:hover:text-gray-300 hover:text-zinc-800 transition-colors">
                          {item.title}
                        </span>
                        <span className="text-base sm:text-lg lg:text-xl dark:text-gray-500 text-zinc-950">
                          ({item.number})
                        </span>
                      </div>
                      <div className="relative">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="w-12 h-12 rounded-full flex items-center justify-center dark:group-hover:bg-gray-800 group-hover:bg-gray-300 transition-colors"
                        >
                          <MdOutlineArrowForward className="w-5 h-5 dark:text-white text-zinc-900 transform rotate-45" />
                        </motion.div>
                      </div>
                    </a>
                  </motion.div>
                ))}
              </nav>

              {/* Social Media Links */}
              <div className="mt-auto pb-10">
                <p className="dark:text-gray-500 text-zinc-900 text-base sm:text-lg">Follow me.</p>
                <div className="mt-4 sm:mt-8 flex flex-wrap gap-6 sm:gap-12">
                  {[
                    { name: "INSTAGRAM", href: "https://www.instagram.com/vivekk.dev/" },
                    { name: "LINKEDIN", href: "https://www.linkedin.com/in/vivek-khule-237682250/" },
                    { name: "TWITTER", href: "https://x.com/VivekKhule" },
                    { name: "GITHUB", href: "https://github.com/VKoder" },
                  ].map((social) => (
                    <motion.a
                      key={social.name}
                      href={social.href}
                      target='_blank'
                      className="group flex items-center gap-2 dark:hover:text-white dark:text-zinc-300 text-zinc-800 hover:text-zinc-900 transition-colors"
                      whileHover={{ x: 4 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      {social.name}
                      <motion.span
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="inline-block"
                      >
                        <MdOutlineArrowForward className="w-4 h-4" />
                      </motion.span>
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}



const scrollbarHideStyles = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export default function HeaderSmallWithStyles(props) {
  return (
    <>
      <style jsx global>{scrollbarHideStyles}</style>
      <HeaderSmall {...props} />
    </>
  );
}

